import _slicedToArray from "/Users/kevin/Projects/tradingblock/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import React, { useEffect, useMemo, useRef } from 'react';
import { useState } from 'react';
import { useCallback } from 'react';
import Select from 'react-select';
import _ from 'lodash';
import { components } from 'react-select/lib/components';
import { useDebounce } from 'use-hooks';
import { SelectDropdownIndicator, AutocompleteControlComponent } from './Autocomplete';
import { SelectMenuPortal, defaultStyles } from './BasicSelect';
export var ReactSelectComponents = components;

var DefaultOptionComponent = function DefaultOptionComponent(props) {
  return React.createElement(ReactSelectComponents.Option, Object.assign({}, props));
};

var tickerFromOption = function tickerFromOption(option) {
  if (option == null) {
    return '';
  }

  var data = option.data;

  if (data != null && typeof data.symbol === 'string' && data.symbol.length > 0) {
    return data.symbol;
  }

  if (option.value != null && String(option.value).length > 0) {
    return String(option.value);
  }

  return option.label != null && option.label !== '' ? String(option.label) : '';
};

var LoadingResults = function LoadingResults(_ref) {
  var noResultsFound = _ref.noResultsFound;
  return useMemo(function () {
    if (noResultsFound) {
      return React.createElement("div", {
        className: "option select__option"
      }, "Symbol not found");
    }

    return React.createElement("div", {
      className: "option select__option"
    }, "Loading...") //TODO: add in loading icon when component memos correctly <i className="fas fa-circle-notch fa-spin mx-2" />
    ;
  }, [noResultsFound]);
};

export var AsyncAutocomplete = function AsyncAutocomplete(props) {
  var OptionComponent = props.OptionComponent,
      defaultValue = props.defaultValue,
      dropdownIcon = props.dropdownIcon,
      placeholder = props.placeholder,
      search = props.search,
      onSelect = props.onSelect,
      debounceTimeout = props.debounceTimeout,
      minimumLength = props.minimumLength,
      initialValues = props.initialValues,
      isClearable = props.isClearable,
      clearOptionsOnSelect = props.clearOptionsOnSelect,
      value = props.value,
      name = props.name,
      isSearchable = props.isSearchable,
      onClear = props.onClear,
      symbol = props.symbol,
      onBlur = props.onBlur,
      charLimit = props.charLimit,
      rest = props.rest,
      rootPortalId = props.rootPortalId,
      closeMenuOnSelect = props.closeMenuOnSelect,
      blurInputOnSelect = props.blurInputOnSelect,
      openMenuOnFocus = props.openMenuOnFocus,
      controlShouldRenderValue = props.controlShouldRenderValue,
      backspaceRemovesValue = props.backspaceRemovesValue,
      commitSymbolToSearchOnBlur = props.commitSymbolToSearchOnBlur,
      clearOnlyWhenInputMatchesSymbol = props.clearOnlyWhenInputMatchesSymbol;
  var Option = useMemo(function () {
    return OptionComponent || DefaultOptionComponent;
  }, [OptionComponent]);
  var selectRef = useRef();
  var selectOptionsRef = useRef([]);
  var inputValueRef = useRef('');
  var keepDraftUntilBlurRef = useRef(false);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      noResultsFound = _useState2[0],
      setNoResultsFound = _useState2[1];

  var _useState3 = useState(undefined),
      _useState4 = _slicedToArray(_useState3, 2),
      queryValue = _useState4[0],
      setQueryValue = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      inputValue = _useState6[0],
      setInputValue = _useState6[1];

  var _useState7 = useState(_.isNumber(minimumLength) ? minimumLength : 2),
      _useState8 = _slicedToArray(_useState7, 1),
      minLength = _useState8[0];

  var initialSelectOpts = useMemo(function () {
    return _(initialValues || []).union([value]).filter(function (v) {
      return v !== undefined;
    }).uniqBy(function (v) {
      return v.value;
    }).value();
  }, [value, initialValues]);

  var _useState9 = useState(initialSelectOpts),
      _useState10 = _slicedToArray(_useState9, 2),
      selectOptions = _useState10[0],
      setOptions = _useState10[1];

  selectOptionsRef.current = selectOptions;
  inputValueRef.current = inputValue;
  var setSelectOptions = useCallback(function (values) {
    return setOptions(values);
  }, []);
  var debouncedQueryValue = useDebounce(queryValue, debounceTimeout || 175);
  useEffect(function () {
    setSelectOptions(initialValues);
  }, [initialValues]);
  useEffect(function () {
    if (debouncedQueryValue && debouncedQueryValue.length >= minLength) {
      search(debouncedQueryValue).then(function (res) {
        setSelectOptions(res);

        if (res.length <= 0) {
          setNoResultsFound(true);
        }
      });
    }
  }, [debouncedQueryValue]);
  var onChange = useCallback(function (val, _ref2) {
    var action = _ref2.action;
    onSelect(val);

    if (commitSymbolToSearchOnBlur) {
      if (action === 'clear' || val == null) {
        keepDraftUntilBlurRef.current = false;
        setInputValue('');
      } else if (action === 'select-option' || action === 'set-value') {
        keepDraftUntilBlurRef.current = true;
      } else {
        keepDraftUntilBlurRef.current = false;

        if (val != null) {
          setInputValue(tickerFromOption(val));
        }
      }
    } else if (action === 'clear' || val == null) {
      setInputValue('');
    } else {
      // Static dropdowns (strike, expiration, etc.): keep input empty so the selected `value`
      // drives the label; copying the label into inputValue makes react-select filter options
      // down to one row and can blank the control (qa always cleared after select).
      setInputValue('');
    }

    var clearOptions = clearOptionsOnSelect && action === 'select-option';

    if (clearOptions || action === 'clear' || action === 'deselect-option' || action === 'remove-value' || action === 'pop-value') {
      setSelectOptions(initialValues || []);
    }

    if (action === 'clear' && onClear) {
      onClear();
    }
  }, [onSelect, onClear, initialValues, clearOptionsOnSelect, setSelectOptions, commitSymbolToSearchOnBlur]);
  var onInputChange = useCallback(function (val, _ref3) {
    var action = _ref3.action;

    switch (action) {
      case 'input-change':
        {
          setInputValue(val);
          setSelectOptions(initialValues || []);
          setQueryValue(val);
          setNoResultsFound(false);
          return undefined;
        }

      case 'set-value':
        {
          if (commitSymbolToSearchOnBlur) {
            return undefined;
          }

          setInputValue('');
          return undefined;
        }

      case 'menu-close':
        {
          if (commitSymbolToSearchOnBlur) {
            return undefined;
          }

          var opts = selectOptionsRef.current;
          var draft = inputValueRef.current;

          if ((!opts || opts.length === 0) && !_.isEmpty(draft)) {
            setInputValue('');
            setSelectOptions(initialValues || []);
          }

          return undefined;
        }

      default:
        return undefined;
    }
  }, [initialValues, setSelectOptions, commitSymbolToSearchOnBlur]);
  var getOptionValue = useCallback(function (option) {
    var optionVal = _.join([_.get(option, 'label'), _.get(option, 'searchValue'), _.get(option, 'value')], ' ');

    return optionVal;
  }, []);
  var currentValue = useMemo(function () {
    if (_.isNil(value) && !_.isNil(defaultValue)) {
      var initValue = _.find(selectOptions, function (o) {
        return o.data === defaultValue.data;
      });

      return initValue;
    }

    return value || null;
  }, [defaultValue, value, selectOptions]);
  var formatOptionLabel = useCallback(function (option, labelMeta) {
    if (!_.isNil(defaultValue) && _.isNil(value) && currentValue && currentValue.value === option.value && labelMeta.context === 'value') {
      return React.createElement("div", {
        className: "badge select__placeholder"
      }, placeholder);
    }

    return option.label;
  }, [currentValue, defaultValue, placeholder, value]);
  var styles = useMemo(function () {
    return {
      menuPortal: defaultStyles.menuPortal
    };
  }, [window.scrollY, defaultStyles.menuPortal]);
  useEffect(function () {
    if (commitSymbolToSearchOnBlur && keepDraftUntilBlurRef.current) {
      return;
    } // Only ticker/search callers pass `symbol` (e.g. HttpAutocomplete). Dropdowns like order-leg expiration
    // omit it entirely — previously `!symbol` cleared input every render and hid the selected label (TBFE-2305).


    if (symbol === undefined) {
      return;
    }

    if (!symbol) {
      setInputValue('');
      return;
    }

    setInputValue(symbol);
    setSelectOptions(initialValues || []);
  }, [symbol, commitSymbolToSearchOnBlur, initialValues]);
  var handleBlur = useCallback(function (_e) {
    if (commitSymbolToSearchOnBlur) {
      var hadPendingSelection = keepDraftUntilBlurRef.current;
      keepDraftUntilBlurRef.current = false;

      if (hadPendingSelection && symbol) {
        setInputValue(symbol);
      }
    }

    if (onBlur) {
      onBlur();
    }
  }, [commitSymbolToSearchOnBlur, symbol, onBlur]);

  var inputFormat = function inputFormat(iv) {
    if (charLimit) {
      return iv.slice(0, charLimit);
    }

    return iv;
  };

  var committedTickerForClear = symbol && symbol.length > 0 ? symbol : currentValue ? tickerFromOption(currentValue) : '';
  var formattedInput = inputFormat(inputValue);
  var inputSyncedForClear = committedTickerForClear.length === 0 || formattedInput.trim() === '' || formattedInput.trim().toUpperCase() === committedTickerForClear.toUpperCase();
  var baseIsClearable = _.isBoolean(isClearable) ? isClearable : true;
  var effectiveIsClearable = clearOnlyWhenInputMatchesSymbol ? Boolean(baseIsClearable && currentValue && inputSyncedForClear) : baseIsClearable;
  var searchable = _.isBoolean(isSearchable) ? isSearchable : true;
  return React.createElement(Select, Object.assign({}, rest || {}, {
    ref: selectRef,
    placeholder: placeholder,
    value: currentValue,
    inputValue: formattedInput,
    onInputChange: onInputChange,
    menuPlacement: 'auto',
    formatOptionLabel: formatOptionLabel,
    onChange: onChange,
    name: name,
    isClearable: effectiveIsClearable,
    isSearchable: searchable,
    filterOption: searchable ? undefined : function () {
      return true;
    },
    options: selectOptions,
    className: "basic-multi-select",
    getOptionValue: getOptionValue,
    classNamePrefix: "select",
    menuShouldBlockScroll: 'true',
    styles: styles,
    menuPortalTarget: document.getElementById(rootPortalId ? rootPortalId : 'portal-root'),
    noOptionsMessage: function noOptionsMessage(e) {
      return e.inputValue ? '' : null;
    },
    onKeyDown: function onKeyDown(e) {
      if (e.key === 'Enter') {
        search(inputValue, 1).then(function (res) {
          if (res.length > 0 && res[0].value && res[0].value.toUpperCase() === inputValue.toUpperCase()) {
            onChange(res[0], {
              action: 'set-value'
            }); // Symbol picker (`commitSymbolToSearchOnBlur`): keep focus so the user can immediately edit/replace the ticker.

            if (!commitSymbolToSearchOnBlur && selectRef.current) {
              selectRef.current.blur();
            }
          }
        });
      }
    },
    components: {
      DropdownIndicator: rootPortalId ? undefined : SelectDropdownIndicator(dropdownIcon || 'fa-arrow-right'),
      Control: AutocompleteControlComponent,
      MenuPortal: SelectMenuPortal,
      Option: Option,
      NoOptionsMessage: function NoOptionsMessage() {
        return React.createElement(LoadingResults, {
          noResultsFound: noResultsFound
        });
      }
    },
    onBlur: handleBlur
  }, closeMenuOnSelect !== undefined ? {
    closeMenuOnSelect: closeMenuOnSelect
  } : {}, blurInputOnSelect !== undefined ? {
    blurInputOnSelect: blurInputOnSelect
  } : {}, openMenuOnFocus !== undefined ? {
    openMenuOnFocus: openMenuOnFocus
  } : {}, controlShouldRenderValue !== undefined ? {
    controlShouldRenderValue: controlShouldRenderValue
  } : {}, backspaceRemovesValue !== undefined ? {
    backspaceRemovesValue: backspaceRemovesValue
  } : {}));
};